import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sanitizeSearchQuery } from '../utils/textSanitize';
import { 
  Users, 
  Plus, 
  Search, 
  Filter, 
  Edit, 
  Eye, 
  XCircle, 
  CheckCircle, 
  AlertTriangle,
  X,
  UserCheck,
  UserX,
  Clock
} from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';

const UsersPage = () => {
  const { user, hasPermission, userData, isRole, getEmpresaId } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'view'
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  // Funções do Firebase
  const listUsers = httpsCallable(functions, 'listUsers');
  const createUser = httpsCallable(functions, 'createUser');
  const updateUser = httpsCallable(functions, 'updateUser');
  const deleteUser = httpsCallable(functions, 'deleteUser');

  // Carregar usuários
  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');

      console.log('🔍 UsersPage: Carregando usuários...');

      const result = await listUsers({
        page: pagination.page,
        limit: pagination.limit,
        funcao: roleFilter || undefined,
        ativo: statusFilter === 'ativo' ? true : statusFilter === 'inativo' ? false : undefined,
        search: searchTerm?.trim() ? sanitizeSearchQuery(searchTerm) : undefined
      });

      console.log('🔍 UsersPage: Resultado da listUsers:', result);

      if (result.data?.success) {
        const usersData = result.data.data;
        setUsers(usersData.items || []);
        setPagination(prev => ({
          ...prev,
          total: usersData.pagination?.total || 0,
          totalPages: usersData.pagination?.totalPages || 1
        }));
        
        console.log('✅ UsersPage: Usuários carregados:', usersData.items?.length || 0);
      } else {
        throw new Error(result.data?.message || 'Erro ao carregar usuários');
      }
    } catch (err) {
      console.error('❌ Erro ao carregar usuários:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  // Função para tratar erros
  const getErrorMessage = (error) => {
    console.log('🔍 Analisando erro:', error);
    
    if (error?.code) {
      switch (error.code) {
        case 'permission-denied':
          return 'Você não tem permissão para acessar os usuários.';
        case 'unauthenticated':
          return 'Sessão expirada. Faça login novamente.';
        case 'not-found':
          return 'Usuários não encontrados.';
        case 'failed-precondition':
          return 'Erro de configuração. Contate o administrador.';
        default:
          return error.message || 'Erro desconhecido ao carregar usuários.';
      }
    }
    
    return error?.message || 'Erro ao carregar usuários. Tente novamente.';
  };

  // Carregar usuários quando a página carrega ou filtros mudam
  useEffect(() => {
    if (hasPermission('view_users')) {
      loadUsers();
    }
  }, [pagination.page, statusFilter, roleFilter]);

  // Busca com debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        loadUsers();
      } else {
        setPagination(prev => ({ ...prev, page: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handlers para modal
  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalMode('create');
    setShowModal(true);
    setError('');
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setModalMode('edit');
    setShowModal(true);
    setError('');
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setModalMode('view');
    setShowModal(true);
    setError('');
  };

  const handleSaveUser = async (userData) => {
    try {
      setError('');
      
      if (modalMode === 'create') {
        console.log('🔍 Criando usuário:', userData);
        
        // ✅ CORREÇÃO: Mapear função do frontend para backend e adicionar id_empresa
        const mappedUserData = {
          ...userData,
          displayName: userData.nome,
          funcao: userData.funcao, // Agora já está com o valor correto
          ativo: userData.status === 'ativo',
          id_empresa: getEmpresaId() // ✅ CORREÇÃO: Adicionar id_empresa do contexto
        };
        
        console.log('🔍 Dados enviados para createUser:', mappedUserData);
        
        const result = await createUser(mappedUserData);
        
        if (result.data?.success) {
          setShowModal(false);
          loadUsers();
        } else {
          throw new Error(result.data?.message || 'Erro ao criar usuário');
        }
      } else if (modalMode === 'edit') {
        console.log('🔍 Atualizando usuário:', userData);
        
        const mappedUserData = {
          user_id: selectedUser.id,
          displayName: userData.nome,
          funcao: userData.funcao,
          telefone: userData.telefone,
          ativo: userData.status === 'ativo'
        };
        
        const result = await updateUser(mappedUserData);
        
        if (result.data?.success) {
          setShowModal(false);
          loadUsers();
        } else {
          throw new Error(result.data?.message || 'Erro ao atualizar usuário');
        }
      }
    } catch (err) {
      console.error('❌ Erro ao salvar usuário:', err);
      setError(getErrorMessage(err));
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    try {
      setError('');
      
      if (currentStatus === 'ativo') {
        const result = await deleteUser({ user_id: userId });
        
        if (result.data?.success) {
          loadUsers();
        } else {
          throw new Error(result.data?.message || 'Erro ao desativar usuário');
        }
      } else {
        // Para reativar, usar updateUser
        const result = await updateUser({
          user_id: userId,
          ativo: true
        });
        
        if (result.data?.success) {
          loadUsers();
        } else {
          throw new Error(result.data?.message || 'Erro ao ativar usuário');
        }
      }
    } catch (err) {
      console.error('❌ Erro ao alterar status:', err);
      setError(getErrorMessage(err));
    }
  };

  // Função para mapear função técnica para nome amigável
  const getFunctionDisplayName = (funcao) => {
    const functionMap = {
      'superadmin': 'Super Administrador',
      'admin': 'Administrador',
      'recebedor': 'Recebedor',
      'qualidade': 'Qualidade',
      'fornecedor': 'Fornecedor',
      'manutencao': 'Manutenção',
      'fornecedor_equipamentos': 'Fornecedor de Equipamentos',
    };
    return functionMap[funcao] || funcao;
  };

  // Função para mapear status técnico para nome amigável
  const getStatusDisplayName = (status) => {
    const statusMap = {
      'ativo': 'Ativo',
      'inativo': 'Inativo',
      'pendente': 'Pendente'
    };
    return statusMap[status] || status;
  };

  // Verificar permissões
  if (!hasPermission('view_users')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Acesso negado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Você não tem permissão para visualizar usuários.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Usuários</h1>
          <p className="text-gray-600">
            Gerencie os usuários do sistema. Cada fornecedor cadastrado também entra aqui, com o papel Fornecedor e o e-mail informado no cadastro.
          </p>
        </div>
        {hasPermission('manage_users') && (
          <button
            onClick={handleCreateUser}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Novo Usuário</span>
          </button>
        )}
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder={userData?.funcao === 'superadmin' 
                  ? "Buscar por nome, email ou empresa..." 
                  : "Buscar por nome ou email..."
                }
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todos os Status</option>
            <option value="ativo">Ativo</option>
            <option value="inativo">Inativo</option>
            <option value="pendente">Pendente</option>
          </select>

          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">Todas as Funções</option>
            <option value="admin">Administrador</option>
            <option value="recebedor">Recebedor</option>
            <option value="qualidade">Qualidade</option>
            <option value="fornecedor">Fornecedor</option>
          </select>

          <button
            onClick={() => {
              setSearchTerm('');
              setStatusFilter('');
              setRoleFilter('');
              setPagination(prev => ({ ...prev, page: 1 }));
            }}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
          >
            <Filter className="h-4 w-4" />
            <span>Limpar Filtros</span>
          </button>
        </div>
      </div>

      {/* Mensagem de erro */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Erro</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
              <button
                onClick={loadUsers}
                className="mt-2 text-sm text-red-600 hover:text-red-500 underline"
              >
                Tentar novamente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de usuários */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum usuário encontrado</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter || roleFilter ? 'Tente ajustar os filtros de busca.' : 'Comece criando um novo usuário.'}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuário
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Função
                    </th>
                    {userData?.funcao === 'superadmin' && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Empresa/Fornecedor
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => {
                    const displayName = user.displayName || user.nome || 'Nome não informado';
                    const email = user.email || 'Email não informado';
                    const funcao = user.funcao || 'Função não definida';
                    const status = user.ativo ? 'ativo' : 'inativo';
                    const telefone = user.telefone || 'Não informado';

                    return (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10">
                              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                <Users className="h-5 w-5 text-blue-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {displayName}
                              </div>
                              <div className="text-sm text-gray-500">
                                {email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            funcao === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                            funcao === 'admin' ? 'bg-red-100 text-red-800' :
                            funcao === 'gerente' ? 'bg-blue-100 text-blue-800' :
                            funcao === 'recebedor' ? 'bg-green-100 text-green-800' :
                            funcao === 'qualidade' ? 'bg-cyan-100 text-cyan-800' :
                            funcao === 'fornecedor' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {getFunctionDisplayName(funcao)}
                          </span>
                        </td>
                        {userData?.funcao === 'superadmin' && (
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {user.nome_empresa || user.nome_fornecedor || '-'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {user.nome_empresa && 'Empresa'}
                              {user.nome_fornecedor && 'Fornecedor'}
                              {!user.nome_empresa && !user.nome_fornecedor && 'Sem vínculo'}
                            </div>
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            status === 'ativo' ? 'bg-green-100 text-green-800' :
                            status === 'inativo' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {status === 'ativo' ? (
                              <>
                                <UserCheck className="h-3 w-3 mr-1" />
                                Ativo
                              </>
                            ) : status === 'inativo' ? (
                              <>
                                <UserX className="h-3 w-3 mr-1" />
                                Inativo
                              </>
                            ) : (
                              <>
                                <Clock className="h-3 w-3 mr-1" />
                                Pendente
                              </>
                            )}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {telefone}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleViewUser(user)}
                              className="text-blue-600 hover:text-blue-900"
                              title="Visualizar"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {hasPermission('manage_users') && (
                              <>
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="text-green-600 hover:text-green-900"
                                  title="Editar"
                                >
                                  <Edit className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleToggleStatus(user.id, user.status)}
                                  className={user.status === 'ativo' ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}
                                  title={user.status === 'ativo' ? 'Desativar' : 'Ativar'}
                                >
                                  {user.status === 'ativo' ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Próximo
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando página <span className="font-medium">{pagination.page}</span> de{' '}
                      <span className="font-medium">{pagination.totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                        disabled={pagination.page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Anterior
                      </button>
                      <button
                        onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.totalPages, prev.page + 1) }))}
                        disabled={pagination.page === pagination.totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* User Modal */}
      {showModal && (
        <UserModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          onSave={handleSaveUser}
          user={selectedUser}
          mode={modalMode}
          error={error}
          setError={setError}
          userData={userData}
        />
      )}
    </div>
  );
};

// ✅ CORREÇÃO: Modal com valores corretos no select
const UserModal = ({ isOpen, onClose, onSave, user, mode, error, setError, userData }) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    funcao: '',
    telefone: '',
    status: 'ativo'
  });
  const [loading, setLoading] = useState(false);

  // ✅ NOVA FUNÇÃO: Retornar opções de função baseadas no perfil do usuário logado
  const getAvailableFunctions = () => {
    const userRole = userData?.funcao;
    
    if (userRole === 'superadmin') {
      return [
        { value: 'superadmin', label: 'Super Administrador' }
      ];
    } else if (userRole === 'admin') {
      return [
        { value: 'admin', label: 'Administrador' },
        { value: 'recebedor', label: 'Recebedor' },
        { value: 'qualidade', label: 'Qualidade' },
        { value: 'producao', label: 'Produção' },
        { value: 'manutencao', label: 'Manutenção' },
        { value: 'fornecedor_equipamentos', label: 'Fornecedor de Equipamentos' },
      ];
    } else if (userRole === 'fornecedor') {
      return [
        { value: 'fornecedor', label: 'Fornecedor' }
      ];
    }
    
    return [];
  };

  useEffect(() => {
    if (user && (mode === 'edit' || mode === 'view')) {
      setFormData({
        nome: user.nome || user.displayName || '',
        email: user.email || '',
        funcao: user.funcao || '',
        telefone: user.telefone || '',
        status: user.ativo ? 'ativo' : 'inativo'
      });
    } else {
      setFormData({
        nome: '',
        email: '',
        funcao: '',
        telefone: '',
        status: 'ativo'
      });
    }
  }, [user, mode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Erro no modal:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {mode === 'create' ? 'Novo Usuário' : 
             mode === 'edit' ? 'Editar Usuário' : 'Visualizar Usuário'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nome Completo *
            </label>
            <input
              type="text"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={mode === 'view'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={mode === 'view'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Função *
            </label>
            <select
              value={formData.funcao}
              onChange={(e) => setFormData({ ...formData, funcao: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
              disabled={mode === 'view'}
            >
              <option value="">Selecione</option>
              {getAvailableFunctions().map(func => (
                <option key={func.value} value={func.value}>
                  {func.label}
                </option>
              ))}
            </select>
            {getAvailableFunctions().length === 0 && (
              <p className="text-xs text-red-500 mt-1">
                Seu perfil não tem permissão para criar usuários
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Telefone
            </label>
            <input
              type="tel"
              value={formData.telefone}
              onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={mode === 'view'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={mode === 'view'}
            >
              <option value="ativo">Ativo</option>
              <option value="inativo">Inativo</option>
              <option value="pendente">Pendente</option>
            </select>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              {mode === 'view' ? 'Fechar' : 'Cancelar'}
            </button>
            {mode !== 'view' && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : 'Salvar'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;

